import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowRight, HeartPulse, Cpu, ShieldCheck, Sparkles, CheckCircle2, Mic, BookOpen, Star } from 'lucide-react';
import { RecentlyViewed } from '../../components/RecentlyViewed';
import { LogoOrMark } from '../../components/Lettermark';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const HERO = 'https://images.unsplash.com/photo-1587556930799-8dca6fad6d41?crop=entropy&cs=srgb&fm=jpg&q=80&w=1200';

const PublicHome = () => {
  const [meta, setMeta] = useState(null);
  const [featured, setFeatured] = useState([]);
  const [episodes, setEpisodes] = useState([]);
  const [guides, setGuides] = useState([]);
  const [spotlight, setSpotlight] = useState([]);

  useEffect(() => {
    axios.get(`${API}/public/meta`).then((r) => setMeta(r.data)).catch(() => {});
    axios.get(`${API}/public/suppliers?premium=true&page_size=8`).then((r) => setFeatured(r.data.items || [])).catch(() => {});
    axios.get(`${API}/public/podcast`).then((r) => setEpisodes(r.data.episodes || [])).catch(() => {});
    axios.get(`${API}/public/blog?guides=1&per_page=3`).then((r) => setGuides(r.data.items || [])).catch(() => {});
    axios.get(`${API}/public/featured-products?limit=8`).then((r) => setSpotlight(r.data.items || [])).catch(() => {});
  }, []);

  return (
    <div data-testid="public-home">
      {/* Hero */}
      <section className="bg-white border-b border-[#E5E7EB]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-20 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#00A79D] bg-[#00A79D]/10 px-3 py-1 rounded-full mb-5">
              <Sparkles className="w-3.5 h-3.5" /> Independent care technology guidance
            </span>
            <h1 className="font-heading text-4xl sm:text-5xl tracking-tight font-extrabold text-gray-900 leading-tight">
              Choosing care technology, <span className="text-[#00A79D]">made simple.</span>
            </h1>
            <p className="text-lg text-gray-600 mt-5 max-w-xl">
              No single care manager can hold all the depth needed to choose tech well. We carry the complexity -
              and hand you a tailored shortlist that's right for your service.
            </p>
            <div className="flex flex-wrap gap-3 mt-8">
              <Link to="/how-to-choose" className="inline-flex items-center gap-2 bg-[#00A79D] hover:bg-[#00857D] text-white font-semibold px-6 py-3 rounded-md transition-colors" data-testid="home-cta-shortlist">
                Get my shortlist <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/browse" className="inline-flex items-center gap-2 border border-[#E5E7EB] hover:border-[#00A79D] text-gray-800 font-semibold px-6 py-3 rounded-md transition-colors" data-testid="home-cta-browse">
                Browse the directory
              </Link>
            </div>
            {meta && (
              <p className="text-sm text-gray-500 mt-5">{meta.total_suppliers.toLocaleString()} suppliers · {meta.categories.length} categories · impartial &amp; independent</p>
            )}
          </div>
          <div className="relative">
            <img src={HERO} alt="Care technology" className="rounded-lg border border-[#E5E7EB] object-cover w-full h-80 lg:h-96" />
          </div>
        </div>
      </section>

      {/* Recently viewed (returning visitors) */}
      <RecentlyViewed className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-10" />

      {/* Spotlight - rotating featured products */}
      {spotlight.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12" data-testid="home-spotlight">
          <div className="flex items-center justify-between mb-5">
            <h2 className="font-heading text-2xl font-bold text-gray-900 flex items-center gap-2"><Star className="w-5 h-5 fill-amber-400 text-amber-500" /> Spotlight</h2>
            <Link to="/browse?mode=products" className="text-sm font-semibold text-[#00857D] hover:underline inline-flex items-center gap-1">See all products <ArrowRight className="w-4 h-4" /></Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {spotlight.slice(0, 4).map((p) => (
              <Link key={p.slug} to={`/products/${p.slug}`} data-testid={`spotlight-product-${p.slug}`}
                className="group bg-white border border-amber-200 ring-1 ring-amber-100 rounded-lg overflow-hidden hover:border-amber-400 hover:shadow-sm transition-all flex flex-col">
                {p.image && <div className="aspect-video bg-[#F1F5F4] overflow-hidden"><img src={p.image} alt={p.name} className="w-full h-full object-cover" onError={(e) => { e.target.parentElement.style.display = 'none'; }} /></div>}
                <div className="p-4 flex flex-col flex-1">
                  <span className="inline-flex items-center gap-1 self-start text-[10px] font-bold text-amber-700 bg-amber-50 border border-amber-300 px-2 py-0.5 rounded-full mb-2"><Star className="w-3 h-3 fill-amber-400 text-amber-500" /> Featured</span>
                  <h3 className="font-semibold text-gray-900 group-hover:text-[#00857D] transition-colors">{p.name}</h3>
                  <p className="text-xs text-gray-500 mt-0.5">by {p.supplier_name}</p>
                  {p.description && <p className="text-sm text-gray-500 mt-2 line-clamp-2 flex-1">{p.description}</p>}
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Dual path */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <h2 className="font-heading text-3xl tracking-tight font-bold text-gray-900 text-center">Where would you like to start?</h2>
        <div className="grid md:grid-cols-2 gap-6 mt-10">
          <Link to="/browse" className="group bg-white border border-[#E5E7EB] rounded-lg p-8 hover:border-[#00A79D] hover:shadow-sm transition-all" data-testid="home-path-provider">
            <div className="w-12 h-12 rounded-md bg-[#00A79D]/10 flex items-center justify-center mb-4"><HeartPulse className="w-6 h-6 text-[#00A79D]" /></div>
            <h3 className="font-heading text-2xl font-semibold text-gray-900">I'm a care provider</h3>
            <p className="text-gray-600 mt-2">Find the right tech for your service, compare options, see what's emerging, and get a tailored shortlist without the sales pitch.</p>
            <span className="inline-flex items-center gap-1.5 text-[#00A79D] font-semibold mt-4 group-hover:gap-2.5 transition-all">Help me choose <ArrowRight className="w-4 h-4" /></span>
          </Link>
          <Link to="/browse?claim=1" className="group bg-white border border-[#E5E7EB] rounded-lg p-8 hover:border-[#00A79D] hover:shadow-sm transition-all" data-testid="home-path-supplier">
            <div className="w-12 h-12 rounded-md bg-[#00A79D]/10 flex items-center justify-center mb-4"><Cpu className="w-6 h-6 text-[#00A79D]" /></div>
            <h3 className="font-heading text-2xl font-semibold text-gray-900">I'm a tech supplier</h3>
            <p className="text-gray-600 mt-2">Get listed in front of care buyers who are actively choosing. A free presence for every supplier, with premium listings for partners.</p>
            <span className="inline-flex items-center gap-1.5 text-[#00A79D] font-semibold mt-4 group-hover:gap-2.5 transition-all">List your product <ArrowRight className="w-4 h-4" /></span>
          </Link>
        </div>
      </section>

      {/* Value props */}
      <section className="bg-white border-y border-[#E5E7EB]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 grid md:grid-cols-3 gap-8">
          {[
            { icon: ShieldCheck, t: 'Independent', d: 'We don\'t sell software. Our only job is to help you choose what\'s genuinely right.' },
            { icon: CheckCircle2, t: 'We carry the complexity', d: 'Interoperability, fair pricing, full stacks vs standalone, we hold the detail so you don\'t.' },
            { icon: Sparkles, t: 'Decision-grade intelligence', d: 'Organised, current, real supplier knowledge, not the public data anyone can scrape.' },
          ].map((v) => (
            <div key={v.t}>
              <v.icon className="w-7 h-7 text-[#00A79D] mb-3" />
              <h3 className="font-heading text-xl font-semibold text-gray-900">{v.t}</h3>
              <p className="text-gray-600 mt-1.5 text-sm">{v.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Buyer's guides strip */}
      {guides.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16" data-testid="home-guides-strip">
          <div className="flex items-end justify-between mb-8 gap-4">
            <div>
              <div className="inline-flex items-center gap-2 text-[#00857D] font-semibold text-sm mb-2"><BookOpen className="w-4 h-4" /> Independent buyer's guides</div>
              <h2 className="font-heading text-3xl tracking-tight font-bold text-gray-900">Choose with confidence</h2>
              <p className="text-gray-600 mt-2 max-w-2xl">In-depth, no-paid-inclusion guides comparing the leading UK suppliers in each category.</p>
            </div>
            <Link to="/knowledge-hub?guides=1" className="hidden sm:inline-flex text-[#00A79D] font-semibold text-sm items-center gap-1.5 shrink-0" data-testid="home-guides-all">All guides <ArrowRight className="w-4 h-4" /></Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {guides.map((g) => (
              <Link key={g.slug} to={`/knowledge-hub/${g.slug}`} className="group bg-white border border-[#E5E7EB] rounded-xl overflow-hidden hover:border-[#00A79D]/50 hover:shadow-md transition-all flex flex-col" data-testid={`home-guide-${g.slug}`}>
                <div className="aspect-[16/9] bg-[#F1F5F4] overflow-hidden">
                  {g.image
                    ? <img src={g.image} alt={g.title} loading="lazy" onError={(e) => { e.target.style.display = 'none'; }} className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-300" />
                    : <div className="w-full h-full flex items-center justify-center"><BookOpen className="w-10 h-10 text-[#00A79D]/40" /></div>}
                </div>
                <div className="p-5 flex flex-col flex-1">
                  <div className="flex flex-wrap gap-1.5 mb-2">
                    <span className="text-[10px] font-semibold uppercase tracking-wide text-[#00857D] bg-[#00A79D]/10 px-2 py-0.5 rounded">Buyer's Guide</span>
                    {(g.categories || []).slice(0, 1).map((c) => (
                      <span key={c} className="text-[10px] font-semibold uppercase tracking-wide text-gray-500 bg-gray-100 px-2 py-0.5 rounded">{c}</span>
                    ))}
                  </div>
                  <h3 className="font-heading font-bold text-gray-900 leading-snug group-hover:text-[#00857D] transition-colors">{g.title}</h3>
                  {g.excerpt && <p className="text-sm text-gray-600 mt-2 line-clamp-2 flex-1">{g.excerpt}</p>}
                  <span className="inline-flex items-center gap-1.5 text-[#00A79D] font-semibold text-sm mt-4 group-hover:gap-2.5 transition-all">Read the guide <ArrowRight className="w-4 h-4" /></span>
                </div>
              </Link>
            ))}
          </div>
          <Link to="/knowledge-hub?guides=1" className="sm:hidden inline-flex text-[#00A79D] font-semibold text-sm items-center gap-1.5 mt-6" data-testid="home-guides-all-mobile">All guides <ArrowRight className="w-4 h-4" /></Link>
        </section>
      )}

      {/* Podcast promo strip */}
      {episodes.length > 0 && (
        <section className="bg-[#1A3A5F] text-white" data-testid="home-podcast-strip">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
            <div className="flex flex-col lg:flex-row lg:items-center gap-8 lg:gap-12">
              <div className="lg:max-w-md">
                <div className="inline-flex items-center gap-2 text-[#2fd0c6] font-semibold text-sm mb-3"><Mic className="w-4 h-4" /> The Care Tech Compare Podcast</div>
                <h2 className="font-heading text-3xl tracking-tight font-bold">Hear it from the suppliers themselves</h2>
                <p className="text-white/70 mt-3">Founders and teams behind {episodes.length} care tech tools share how their products really work. Filter the directory to suppliers with a podcast episode.</p>
                <div className="flex flex-wrap gap-3 mt-6">
                  <Link to="/podcast" className="inline-flex items-center gap-2 bg-[#00A79D] hover:bg-[#00857D] text-white font-semibold px-5 py-2.5 rounded-md transition-colors" data-testid="home-podcast-explore">
                    Explore the podcast <ArrowRight className="w-4 h-4" />
                  </Link>
                  <Link to="/browse?podcast=1" className="inline-flex items-center gap-2 border border-white/25 hover:border-[#2fd0c6] text-white font-semibold px-5 py-2.5 rounded-md transition-colors" data-testid="home-podcast-browse">
                    <Mic className="w-4 h-4" /> Suppliers with a podcast
                  </Link>
                </div>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex gap-4 overflow-x-auto pb-2 lg:grid lg:grid-cols-3 lg:overflow-visible">
                  {episodes.slice(0, 6).map((ep) => (
                    <Link key={ep.slug} to={ep.post_url} className="shrink-0 w-56 lg:w-auto bg-white/5 border border-white/10 rounded-xl p-4 hover:bg-white/10 hover:border-[#2fd0c6]/40 transition-all flex flex-col" data-testid={`home-podcast-ep-${ep.slug}`}>
                      <div className="flex items-center gap-2.5 mb-3">
                        <LogoOrMark logo={ep.logo} name={ep.supplier_name} imgClassName="h-8 w-auto max-w-[90px] object-contain bg-white rounded px-1" markClassName="h-8 w-8 text-xs rounded-md" />
                        <span className="text-xs text-white/60 truncate">{ep.supplier_name}</span>
                      </div>
                      <p className="text-sm font-semibold leading-snug line-clamp-2">{ep.title}</p>
                      <span className="inline-flex items-center gap-1 text-xs text-[#2fd0c6] font-semibold mt-3">Read &amp; listen <ArrowRight className="w-3 h-3" /></span>
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Featured premium suppliers */}
      {featured.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="flex items-end justify-between mb-8">
            <h2 className="font-heading text-3xl tracking-tight font-bold text-gray-900">Featured suppliers</h2>
            <Link to="/browse" className="text-[#00A79D] font-semibold text-sm inline-flex items-center gap-1.5">View all <ArrowRight className="w-4 h-4" /></Link>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {featured.map((s) => (
              <Link key={s.slug} to={`/suppliers/${s.slug}`} className="bg-white border border-[#E5E7EB] rounded-lg p-5 hover:border-[#00A79D] hover:shadow-sm transition-all flex flex-col" data-testid={`home-featured-${s.slug}`}>
                {s.logo ? <img src={s.logo} alt={s.name} className="h-10 w-auto object-contain self-start mb-3" onError={(e) => { e.target.style.display = 'none'; }} /> : <div className="h-10 mb-3 flex items-center"><span className="font-heading font-bold text-gray-900">{s.name}</span></div>}
                <span className="text-xs font-semibold text-[#00A79D] bg-[#00A79D]/10 px-2 py-0.5 rounded-full self-start mb-2">{s.tier_label || 'Confirmed'}</span>
                <h3 className="font-semibold text-gray-900 text-sm">{s.name}</h3>
                {s.tagline && <p className="text-xs text-gray-500 mt-1 line-clamp-2">{s.tagline}</p>}
              </Link>
            ))}
          </div>
        </section>
      )}
    </div>
  );
};

export default PublicHome;
